import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { publicSupabase } from "@/lib/public-supabase";
import { GoogleReCaptcha } from "@/components/shared/GoogleReCaptcha";

interface PriceRequestFormProps {
  propertyId: string;
  propertyTitle: string;
}

export const PriceRequestForm = ({ propertyId, propertyTitle }: PriceRequestFormProps) => {
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (captchaToken) {
      const timer = setTimeout(() => {
        setCaptchaToken(null);
        setCaptchaReady(false);
      }, 110000);
      return () => clearTimeout(timer);
    }
  }, [captchaToken]);

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
    setCaptchaReady(true);
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setCaptchaReady(false);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.client_name.trim() || formData.client_name.length < 2) {
      newErrors.client_name = "Name must be at least 2 characters long";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.client_email)) {
      newErrors.client_email = "Please enter a valid email address";
    }

    if (formData.client_phone && !/^[\d\s\+\-\(\)]{10,15}$/.test(formData.client_phone.replace(/\s/g, ''))) {
      newErrors.client_phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive",
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Verification Pending",
        description: "Please wait for security verification",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data: verifyData, error: verifyError } = await publicSupabase.functions.invoke('verify-recaptcha', {
        body: { token: captchaToken, expectedAction: 'price_request' }
      });

      if (verifyError || !verifyData?.success) {
        const reason = verifyData?.reason || 'unknown';
        throw new Error(reason === 'low_score' ? 'Please try again' : 'reCAPTCHA verification failed');
      }

      const sanitizedData = {
        client_name: formData.client_name.trim(),
        client_email: formData.client_email.trim().toLowerCase(),
        client_phone: formData.client_phone?.trim() || null,
        message: formData.message?.trim() || null,
      };

      const { error } = await publicSupabase
        .from("price_requests")
        .insert({
          property_id: propertyId,
          name: sanitizedData.client_name,
          email: sanitizedData.client_email,
          phone: sanitizedData.client_phone || '',
          message: sanitizedData.message,
        });

      if (error) throw error;

      await publicSupabase.functions.invoke('send-notification', {
        body: {
          type: 'price_request',
          data: {
            name: sanitizedData.client_name,
            phone: sanitizedData.client_phone,
            email: sanitizedData.client_email,
            propertyTitle,
            message: sanitizedData.message,
          }
        }
      });

      toast({
        title: "Price Request Sent",
        description: "We'll get back to you with pricing details soon!",
      });

      setFormData({
        client_name: "",
        client_email: "",
        client_phone: "",
        message: "",
      });
      setCaptchaToken(null);
      setCaptchaReady(false);
      setErrors({});
    } catch (error) {
      console.error("Error submitting price request:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send price request. Please try again.",
        variant: "destructive",
      });
      setCaptchaToken(null);
      setCaptchaReady(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="space-y-4">
      <GoogleReCaptcha
        onVerify={handleCaptchaVerify}
        onError={handleCaptchaError}
        action="price_request"
      />

      <div>
        <h3 className="text-lg font-semibold mb-2">Request Price Information</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Get detailed pricing information for: {propertyTitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="client_name">Full Name *</Label>
            <Input
              id="client_name"
              name="client_name"
              value={formData.client_name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
              className={errors.client_name ? "border-destructive" : ""}
            />
            {errors.client_name && (
              <p className="text-sm text-destructive mt-1">{errors.client_name}</p>
            )}
          </div>
          <div>
            <Label htmlFor="client_email">Email *</Label>
            <Input
              id="client_email"
              name="client_email"
              type="email"
              value={formData.client_email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              className={errors.client_email ? "border-destructive" : ""}
            />
            {errors.client_email && (
              <p className="text-sm text-destructive mt-1">{errors.client_email}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="client_phone">Phone Number</Label>
          <Input
            id="client_phone"
            name="client_phone"
            type="tel"
            value={formData.client_phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            className={errors.client_phone ? "border-destructive" : ""}
          />
          {errors.client_phone && (
            <p className="text-sm text-destructive mt-1">{errors.client_phone}</p>
          )}
        </div>

        <div>
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Any specific questions or requirements..."
            rows={3}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={loading || !captchaReady}
        >
          {loading ? "Sending..." : "Request Price Information"}
        </Button>
      </form>
    </div>
  );
};
